import React, { useEffect, useMemo, useCallback } from 'react';
import T from 'i18n';
import {
  FilterListOutlined,
  Group,
  InfoOutlined,
  KeyboardArrowUp,
  ContentCopy,
  Source,
  DragIndicator,
  ThumbUp,
  ThumbDown,
  Send,
  RestartAltOutlined,
} from '@mui/icons-material';
import {
  Avatar,
  Badge,
  Fab,
  Grid,
  Tooltip,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Close } from '@mui/icons-material';
import { useSelector } from 'react-redux';
// Removed MoveDocument/assignDocuments and selection icons per simplified dropdown
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useFetchMatches } from '../redux/fetchMatches';
import { selectParams } from 'common/selectors';
import SearchAnswerContainer from './SearchAnswerContainer';
import { SearchOptionsTooltip } from './SearchOptionsTooltip';
import { searchCenterLocation } from './enums';
import SearchMatchesDisplayContainer from './SearchMatchesDisplayContainer';
import { makeStyles } from '@mui/styles';
import SearchErrorPage from './SearchErrorPage';
import { useSetAISearchState } from '../redux/setAISearchState';
import DocSummary from './DocSummary';
import { removeFormatSuffix } from './utils/formatUtils';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import RelevantFilesTable from './RelevantFilesTable';
import { useUpdateSearchHistoryItem } from '../redux/updateSearchHistoryItem';
import { useToasts } from 'react-toast-notifications';

const useStyles = makeStyles({
  answersContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '1rem 1rem 1rem 0',
  },
  answersSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  answersSectionAvatar: {
    width: 28,
    height: 28,
  },
  answersSectionHeading: {
    color: '#0000007a',
    fontWeight: 600,
  },
  copyWrapper: {
    position: 'relative',
    display: 'inline-block',
    '&:hover $copyButton': {
      opacity: 1,
    },
  },
  copyButton: {
    position: 'absolute',
    top: -5,
    right: 0,
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
    background: 'rgba(255, 255, 255, 0.9)',
    border: '1px solid #ddd',
    borderRadius: 4,
    zIndex: 1,
  },
  scrollToTopButton: {
    position: 'absolute',
    bottom: 0,
    right: '1rem',
    zIndex: 1000,
  },
});

const blinkAnimation = `@keyframes blinker {
  50% {
    opacity: 0;
  }
}`;

const searchingTextStyle = {
  animation: `blinker 1s linear infinite`,
  fontWeight: 600,
  color: '#0000007a',
};

const DraggableSourceFileItem = React.memo(
  ({
    file,
    allFiles,
    selectedIdsRef,
    isSelected,
    onItemClick,
    onCmdEnterToggle,
    citationCount,
  }: {
    file: { id: string; name: string };
    allFiles: Array<{ id: string; name: string }>;
    selectedIdsRef: React.MutableRefObject<string[]>;
    isSelected: boolean;
    onItemClick: (e: React.MouseEvent) => void;
    onCmdEnterToggle: () => void;
    citationCount: number;
  }) => {
    const [, drag, preview] = useDrag(() => ({
      type: 'DOCUMENT',
      canDrag: () => {
        const selectedIds = selectedIdsRef.current || [];
        // Allow dragging only when selection exists and this item is selected
        return selectedIds.length > 0 && selectedIds.includes(file.id);
      },
      item: () => {
        // Drag all selected (dragging is enabled only when selected)
        const selectedIds = selectedIdsRef.current || [];
        const useIds = selectedIds;
        const docs = allFiles
          .filter((f) => useIds.includes(f.id))
          .map((f) => ({ id: f.id, name: f.name }));
        return {
          documentIds: useIds,
          dragRow: { id: file.id, name: file.name },
          type: 'DOCUMENT',
          isAttachment: true,
          attachmentDocuments: docs,
        };
      },
      options: { dropEffect: 'copy' },
    }));

    useEffect(() => {
      preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    return (
      <ListItem
        ref={drag}
        onClick={onItemClick}
        onKeyDown={(e: React.KeyboardEvent) => {
          if ((e.metaKey || e.ctrlKey) && (e.key === 'Enter' || e.keyCode === 13)) {
            e.preventDefault();
            onCmdEnterToggle();
          }
        }}
        tabIndex={0}
        role="button"
        sx={{
          py: 0.5,
          cursor: 'pointer',
          ...(isSelected && { backgroundColor: 'action.selected' }),
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {isSelected && <DragIndicator color="primary" sx={{ mr: 1, flexShrink: 0 }} />}
        <ListItemText primary={file.name} secondary={`ID: ${file.id}`} sx={{ flex: 1, mr: 2 }} />
        <Box
          sx={{
            alignSelf: 'center',
            ml: 1,
            px: 1,
            py: '2px',
            bgcolor: '#F7F7F8',
            border: '1px solid #85A0F1',
            borderRadius: '16px',
            fontSize: 12,
            lineHeight: 1,
            minWidth: 24,
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          {citationCount || 0}
        </Box>
      </ListItem>
    );
  },
);

interface SearchContainerProps {
  selectedMatches: Array<any>;
  scrollBarRef: any;
  searchContainerId: string;
  startNewSearch: any;
  processQuestions: (
    isSuggestedQuestions?: any,
    inpVal?: string,
    fileSidebarAskAI?: boolean,
    firstChat?: boolean,
    outputType?: string,
  ) => void;
  askAIHandler: any;
  fileSidebarAskAI: boolean;
  gridView?: boolean;
  isTranscriptPage?: boolean;
  directAnswers?: boolean;
  onRestartFromQuestion?: (questionText: string, answerFilters?: any) => void;
}

export default function SearchContainer({
  selectedMatches,
  scrollBarRef,
  searchContainerId,
  startNewSearch,
  processQuestions,
  askAIHandler,
  fileSidebarAskAI,
  gridView,
  isTranscriptPage,
  directAnswers,
  onRestartFromQuestion,
}: SearchContainerProps) {
  const classes = useStyles();
  const { fetchMatchDocumentsPending } = useFetchMatches();
  const { searchMatchesInput, searchAnsChat, searchSuggestedQuestions, directAnswersFilters } =
    useSetAISearchState();
  const { file } = useSelector(selectParams);
  const { updateSearchHistoryItem: updateSearchHistoryItemAction } = useUpdateSearchHistoryItem();
  const { addToast } = useToasts();
  const [selectedContainerId, setSelectedContainerId] = React.useState<any>(null);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [sourceMenuAnchorEl, setSourceMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [sourceMenuFiles, setSourceMenuFiles] = React.useState<
    Array<{ id: string; name: string; citationsCount?: number }>
  >([]);
  const [sourceMenuPlacement, setSourceMenuPlacement] = React.useState<
    'bottom-start' | 'top-start'
  >('bottom-start');
  const [sourceMenuSelectedIds, setSourceMenuSelectedIds] = React.useState<string[]>([]);
  const sourceMenuSelectedIdsRef = React.useRef<string[]>([]);
  const [sourceMenuCitationsCount, setSourceMenuCitationsCount] = React.useState<
    Record<string, number>
  >({});
  const totalSourceMenuCitations = useMemo(() => {
    if (!Array.isArray(sourceMenuFiles) || sourceMenuFiles.length === 0) return 0;
    return sourceMenuFiles.reduce((sum: number, sourceFile: { citationsCount?: number }) => {
      return sum + (typeof sourceFile.citationsCount === 'number' ? sourceFile.citationsCount : 0);
    }, 0);
  }, [sourceMenuFiles]);
  useEffect(() => {
    sourceMenuSelectedIdsRef.current = sourceMenuSelectedIds;
  }, [sourceMenuSelectedIds]);

  const matchesData = searchMatchesInput?.find((itm: any) => itm.matches) || [];
  const showMatchesResp = searchMatchesInput.some((itm: any) => itm.showMatchesResp);
  const isError = matchesData && matchesData[0]?.isError;
  const [copiedUserTexts, setCopiedUserTexts] = React.useState<Record<string, boolean>>({});
  const [showFeedbackInput, setShowFeedbackInput] = React.useState<Record<string, boolean>>({});
  const [feedbackComment, setFeedbackComment] = React.useState<Record<string, string>>({});
  const [feedbackRating, setFeedbackRating] = React.useState<Record<string, 'up' | 'down' | null>>(
    {},
  );
  // Simple fallback function for legacy compatibility
  const handleMatchesLinkClicked = (index: number, highlight: string) => {
    // This function is kept for compatibility but the actual navigation
    // is now handled directly in SearchAnswerContainer
    console.log('Legacy handleMatchesLinkClicked called', { index, highlight });
  };

  const scrollToTop = useCallback(() => {
    if (scrollBarRef.current) {
      scrollBarRef.current.scrollTop = 0;
    }
  }, [scrollBarRef]);
  const handleThumbUp = useCallback(
    (answerKey: string, searchHistoryId?: string, rawText?: string, answerId?: string) => {
      // Use functional setState to get the current state value
      setFeedbackRating((prev) => {
        const currentRating = prev[answerKey];
        const isCurrentlyLiked = currentRating === 'up';
        const newRating = isCurrentlyLiked ? null : 'up';

        // Submit to API with current state
        if (searchHistoryId) {
          const payload = isCurrentlyLiked
            ? {
              // Remove the feedback by sending empty values
              feedback: '',
              feedbackChat: '',
              feedbackComment: '',
            }
            : {
              // Submit feedback to API (switching from dislike to like, or new like)
              feedback: 'liked',
              feedbackChat: rawText || '',
              feedbackComment: '', // Clear any previous feedback text when switching to like
            };

          const p = updateSearchHistoryItemAction({
            searchHistoryItem: { id: searchHistoryId, ...payload, answerId },
          });

          Promise.resolve(p)
            .then(() => {
              // Success - no toast message
            })
            .catch((error) => {
              console.error('Failed to submit feedback:', error);
              const message = isCurrentlyLiked
                ? T.translate('case.feedbackRemovedFailure') || 'Failed to remove feedback.'
                : T.translate('case.feedbackSubmittedFailure') || 'Failed to submit feedback.';
              addToast(message, {
                appearance: 'error',
                autoDismiss: true,
              });
              // Revert state on error
              setFeedbackRating((revertPrev) => ({
                ...revertPrev,
                [answerKey]: currentRating,
              }));
            });
        }

        return {
          ...prev,
          [answerKey]: newRating,
        };
      });

      // Hide feedback input if it was showing
      setShowFeedbackInput((prev) => ({
        ...prev,
        [answerKey]: false,
      }));

      // Clear feedback text when toggling
      setFeedbackComment((prev) => ({
        ...prev,
        [answerKey]: '',
      }));
    },
    [updateSearchHistoryItemAction, addToast],
  );

  const handleThumbDown = useCallback(
    (
      answerKey: string,
      event?: React.MouseEvent,
      searchHistoryId?: string,
      rawText?: string,
      answerId?: string,
    ) => {
      // Use functional setState to get the current state value
      setFeedbackRating((prev) => {
        const currentRating = prev[answerKey];
        const isCurrentlyDisliked = currentRating === 'down';
        const newRating = isCurrentlyDisliked ? null : 'down';

        // Update feedback input visibility
        setShowFeedbackInput((inputPrev) => ({
          ...inputPrev,
          [answerKey]: !isCurrentlyDisliked, // Show input only when disliking (not removing)
        }));

        // Clear feedback text when removing dislike
        if (isCurrentlyDisliked) {
          setFeedbackComment((commentPrev) => ({
            ...commentPrev,
            [answerKey]: '',
          }));
        }

        // Handle scroll for showing feedback input
        if (!isCurrentlyDisliked && scrollBarRef.current && event) {
          const container = scrollBarRef.current;
          const buttonElement = event.currentTarget as HTMLElement;
          const buttonRect = buttonElement.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();

          // Calculate distance from button to bottom of container
          const distanceFromBottom = containerRect.bottom - buttonRect.bottom;

          // If button is within 150px of the bottom, scroll up by 150px
          if (distanceFromBottom < 150) {
            setTimeout(() => {
              container.scrollTop += 150;
            }, 100); // Small delay to ensure the input box is rendered
          }
        }

        // Submit to API
        if (searchHistoryId) {
          const payload = isCurrentlyDisliked
            ? {
              // Remove the feedback by sending empty values
              feedback: '',
              feedbackChat: '',
              feedbackComment: '',
            }
            : {
              // Submit initial feedback to API (without feedback text) - switching from like to dislike, or new dislike
              feedback: 'disliked',
              feedbackChat: rawText || '',
              feedbackComment: '', // Initially empty, user can add it later
            };

          const p = updateSearchHistoryItemAction({
            searchHistoryItem: { id: searchHistoryId, ...payload, answerId },
          });

          Promise.resolve(p)
            .then(() => {
              // Success - no toast message
            })
            .catch((error) => {
              console.error('Failed to submit feedback:', error);
              const message = isCurrentlyDisliked
                ? T.translate('case.feedbackRemovedFailure') || 'Failed to remove feedback.'
                : T.translate('case.feedbackSubmittedFailure') || 'Failed to submit feedback.';
              addToast(message, {
                appearance: 'error',
                autoDismiss: true,
              });
              // Revert state on error
              setFeedbackRating((revertPrev) => ({
                ...revertPrev,
                [answerKey]: currentRating,
              }));
              setShowFeedbackInput((revertInputPrev) => ({
                ...revertInputPrev,
                [answerKey]: currentRating === 'down',
              }));
            });
        }

        return {
          ...prev,
          [answerKey]: newRating,
        };
      });
    },
    [scrollBarRef, updateSearchHistoryItemAction, addToast],
  );

  const handleFeedbackSubmit = useCallback(
    (answerKey: string, searchHistoryId?: string, rawText?: string, answerId?: string) => {
      const feedback = feedbackComment[answerKey];
      if (feedback && feedback.trim()) {
        // Submit feedback with text to API
        if (searchHistoryId) {
          const payload = {
            feedback: 'disliked',
            feedbackChat: rawText || '',
            feedbackComment: feedback.trim(),
          };

          const p = updateSearchHistoryItemAction({
            searchHistoryItem: { id: searchHistoryId, ...payload, answerId },
          });

          Promise.resolve(p)
            .then(() => {
              // Success - no toast message
            })
            .catch((error) => {
              console.error('Failed to submit feedback:', error);
              addToast(
                T.translate('case.feedbackSubmittedFailure') || 'Failed to submit feedback.',
                {
                  appearance: 'error',
                  autoDismiss: true,
                },
              );
            });
        }

        // Clear the feedback text but keep the input hidden
        setFeedbackComment((prev) => ({
          ...prev,
          [answerKey]: '',
        }));
        setShowFeedbackInput((prev) => ({
          ...prev,
          [answerKey]: false,
        }));
      }
    },
    [feedbackComment, updateSearchHistoryItemAction, addToast],
  );

  const handleFeedbackKeyDown = useCallback(
    (
      e: React.KeyboardEvent,
      answerKey: string,
      searchHistoryId?: string,
      rawText?: string,
      answerId?: string,
    ) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleFeedbackSubmit(answerKey, searchHistoryId, rawText, answerId);
      }
    },
    [handleFeedbackSubmit],
  );

  // Throttle scroll events to prevent excessive re-renders
  const throttledHandleScroll = useCallback(() => {
    let timeoutId: NodeJS.Timeout;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (scrollBarRef.current) {
          const shouldShowButton = scrollBarRef.current.scrollTop > 200;
          setIsScrolled((prev) => (prev !== shouldShowButton ? shouldShowButton : prev));
        }
      }, 100); // Throttle to 100ms
    };
  }, [scrollBarRef]);

  const copyTextToClipboard = useCallback(async (text: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }
      throw new Error('Clipboard API not supported');
    } catch {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      } catch (finalError) {
        console.error('Copy failed:', finalError);
      }
    }
  }, []);

  const handleToggleSourceMenu = useCallback(
    (anchorEl: HTMLElement, files: Array<{ id: string; name: string }> = [], citations: any) => {
      const citationsCountData = citations.reduce(
        (acc: { [x: string]: any }, item: { fileId: string | number }) => {
          acc[item.fileId] = (acc[item.fileId] || 0) + 1;
          return acc;
        },
        {},
      );
      setSourceMenuCitationsCount(citationsCountData);
      try {
        const rect = anchorEl.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const preferredHeight = Math.min(window.innerHeight * 0.6, 400);
        setSourceMenuPlacement(spaceBelow >= preferredHeight ? 'bottom-start' : 'top-start');
      } catch {
        setSourceMenuPlacement('bottom-start');
      }
      setSourceMenuFiles(Array.isArray(files) ? files : []);
      setSourceMenuAnchorEl((prev) => (prev === anchorEl ? null : anchorEl));
      setSourceMenuSelectedIds([]);
    },
    [],
  );

  const handleCloseSourceMenu = useCallback(() => {
    setSourceMenuAnchorEl(null);
    setSourceMenuSelectedIds([]);
  }, []);

  // Close on ESC key only
  useEffect(() => {
    if (!sourceMenuAnchorEl) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSourceMenuAnchorEl(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [sourceMenuAnchorEl]);

  useEffect(() => {
    const throttledHandler = throttledHandleScroll();
    const container = scrollBarRef.current;

    if (container) {
      container.addEventListener('scroll', throttledHandler, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', throttledHandler);
      }
    };
  }, [scrollBarRef, throttledHandleScroll]);
  useEffect(() => {
    const parentContainer = scrollBarRef.current;
    if (parentContainer) {
      const targetElement = document.getElementById(selectedContainerId);
      if (targetElement) {
        const targetTop = targetElement.offsetTop;
        scrollBarRef.current.scrollTop = targetTop - 200;
      }
    }
  }, [scrollBarRef, file, selectedContainerId]);
  // Helper function to render direct answers filters display
  const renderDirectAnswersFilters = useCallback((filterData: any) => {
    if (!filterData) return null;
    const locFilter = filterData.location;
    const fileFilter = filterData.files;
    const filesToCompareFilter = filterData.filesToCompare;
    const hasLocation = Array.isArray(locFilter) && locFilter.length > 0;
    const hasFiles = Array.isArray(fileFilter) && fileFilter.length > 0;
    const hasFilesToCompare =
      Array.isArray(filesToCompareFilter) && filesToCompareFilter.length > 0;

    if (!hasLocation && !hasFiles && !hasFilesToCompare) return null;
    return (
      <div style={{ marginTop: '0.5rem' }}>
        {hasLocation && (
          <Tooltip
            placement="top"
            title={
              <span>
                <b>{T.translate('generic.attachedBundles')}</b>
                {': '}
                {locFilter.map((loc: any) => loc.name).join(', ')}
              </span>
            }
            arrow
          >
            <div style={{ marginTop: '0.25rem' }}>
              <FilterListOutlined fontSize="small" sx={{ mr: '0.5rem', mb: '0.2rem' }} />
              <span>
                <b>{T.translate('generic.attachedBundles')}</b> : {locFilter[0].name}
                {locFilter.length > 1 ? ` & ${locFilter.length - 1} more ...` : ''}
              </span>
            </div>
          </Tooltip>
        )}
        {hasFiles && (
          <Tooltip
            placement="top"
            title={
              <span>
                <b>{T.translate('generic.attachedFiles')}</b>
                {': '}
                {fileFilter.map((bm: any) => bm.name).join(', ')}
              </span>
            }
            arrow
          >
            <div style={{ marginTop: '0.25rem' }}>
              <FilterListOutlined fontSize="small" sx={{ mr: '0.5rem', mb: '0.2rem' }} />
              <span>
                <b>{T.translate('generic.attachedFiles')}</b> : {fileFilter[0].name}
                {fileFilter.length > 1 ? ` & ${fileFilter.length - 1} more ...` : ''}
              </span>
            </div>
          </Tooltip>
        )}
        {hasFilesToCompare && (
          <Tooltip
            placement="top"
            title={
              <span>
                <b>{T.translate('generic.filesCompared')}</b>
                {': '}
                {filesToCompareFilter.map((bm: any) => bm.name).join(', ')}
              </span>
            }
            arrow
          >
            <div style={{ marginTop: '0.25rem' }}>
              <FilterListOutlined fontSize="small" sx={{ mr: '0.5rem', mb: '0.2rem' }} />
              <span>
                <b>{T.translate('generic.filesCompared')}</b> : {filesToCompareFilter[0].name}
                {filesToCompareFilter.length > 1
                  ? ` & ${filesToCompareFilter.length - 1} more ...`
                  : ''}
              </span>
            </div>
          </Tooltip>
        )}
      </div>
    );
  }, []);

  // Memoize heavy rendering components to prevent re-renders during scroll
  const memoizedSearchMatches = useMemo(() => {
    if (
      searchAnsChat?.length > 0 ||
      directAnswers ||
      isError ||
      fileSidebarAskAI ||
      !showMatchesResp ||
      !searchMatchesInput?.length
    ) {
      return null;
    }

    return searchMatchesInput.map((md: any) => (
      <SearchMatchesDisplayContainer
        key={md.id}
        matchesData={md}
        fetchMatchDocumentsPending={fetchMatchDocumentsPending}
        startNewSearch={startNewSearch}
        selectedMatches={selectedMatches}
        setSelectedContainerId={setSelectedContainerId}
        suggestedQuestions={searchSuggestedQuestions}
        processQuestions={processQuestions}
        askAIHandler={askAIHandler}
      />
    ));
  }, [
    searchMatchesInput,
    searchAnsChat,
    directAnswers,
    isError,
    fileSidebarAskAI,
    showMatchesResp,
    fetchMatchDocumentsPending,
    startNewSearch,
    selectedMatches,
    searchSuggestedQuestions,
    processQuestions,
    askAIHandler,
  ]);
  const memoizedSearchAnswers = useMemo(() => {
    if (!searchAnsChat?.length) return null;
    const containsMatches = matchesData?.matches?.length > 0;
    const containsDocuments = matchesData?.documents?.length > 0;
    return searchAnsChat.map((itm: any, index: number) => {
      const itemKey = itm.id || `idx-${index}`;
      const isCopied = copiedUserTexts[itemKey];
      const citations = itm?.citations;
      const effectiveMatches =
        Array.isArray(citations) && citations.length > 0
          ? citations
          : Array.isArray(matchesData?.matches)
            ? matchesData.matches
            : [];
      const effectiveMatchesCount = effectiveMatches.length;

      const previousItem = index > 0 ? searchAnsChat[index - 1] : null;
      const previousUserQuestionText =
        previousItem &&
        previousItem.user !== 'botIcon' &&
        typeof previousItem.text === 'string'
          ? previousItem.text
          : '';

      const answerFiltersForItem =
        itm.user === 'botIcon'
          ? {
              files: Array.isArray(itm.files) ? itm.files : [],
              filesToCompare: Array.isArray(itm.filesToCompare) ? itm.filesToCompare : [],
              location: Array.isArray(itm.location) ? itm.location : [],
            }
          : undefined;

      const canRestartFromQuestion = Boolean(
        previousUserQuestionText && onRestartFromQuestion && answerFiltersForItem,
      );

      // Transform filesToCompare to match compare format if compare is not available
      const effectiveCompare = (() => {
        // First, try to use matchesData?.compare
        if (
          matchesData?.compare &&
          matchesData.compare.files &&
          Array.isArray(matchesData.compare.files) &&
          matchesData.compare.files.length > 0
        ) {
          if (matchesData.compare.files.length) {
            return matchesData.compare;
          }
          return undefined;
        }

        // Fallback to itm.filesToCompare and transform it
        if (
          itm.filesToCompare &&
          Array.isArray(itm.filesToCompare) &&
          itm.filesToCompare.length > 0
        ) {
          if (itm.filesToCompare.length) {
            const fileIds = itm.filesToCompare.map((f: { id: string; name: string }) => f.id);
            return { files: fileIds };
          }
          return undefined;
        }

        // Return undefined if neither is available
        return undefined;
      })();
      return (
        <Grid item lg={12} key={itemKey} id={itm.id}>
          <div
            className={classes.answersContainer}
            style={{
              alignItems: itm.user === 'botIcon' ? 'start' : 'center',
            }}
          >
            {itm.user === 'botIcon' && (
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  itm.contributor ? (
                    <Tooltip title={itm.contributor} placement="top" arrow>
                      <Avatar sx={{ width: 16, height: 16, bgcolor: 'white' }}>
                        <Group sx={{ color: 'GrayText' }} fontSize="small" />
                      </Avatar>
                    </Tooltip>
                  ) : null
                }
              >
                <Avatar
                  className={classes.answersSectionAvatar}
                  sx={{
                    bgcolor: '#85A0F1',
                  }}
                >
                  <Typography>{T.translate('case.tvAvatarText')}</Typography>
                </Avatar>
              </Badge>
            )}

            <div style={{ marginLeft: '0.5rem', flex: 1 }}>
              <div className={classes.answersSection}>
                <div className={classes.copyWrapper} style={{ flex: 1 }}>
                  <Typography
                    className={classes.answersSectionHeading}
                    variant="body1"
                    component="div"
                    style={{
                      fontSize: '13px',
                      ...(itm.user !== 'botIcon' && {
                        padding: '5px 25px 0 30px',
                      }),
                    }}
                  >
                    {itm.user === 'botIcon' ? (
                      T.translate('case.trialview')
                    ) : (
                      <div>
                        <div
                          style={{
                            flex: 1,
                            color: '#777777',
                            backgroundColor: '#ffffff',
                            borderRadius: '5px',
                            padding: '15px',
                            border: fileSidebarAskAI ? '1px solid #e0e0e0' : 'none',
                          }}
                        >
                          {(removeFormatSuffix(itm.text) || '')
                            .split('\n')
                            .map((line: string, idx: number) => (
                              <React.Fragment key={idx}>
                                {line}
                                <br />
                              </React.Fragment>
                            ))}
                        </div>
                        {!searchAnsChat[1]?.searchHistoryId && !directAnswers && (
                          <h4 style={{ paddingTop: '20px' }}>
                            {containsMatches
                              ? T.translate('case.matchesFoundMsg', {
                                  matchesCount: matchesData.matches.length,
                                })
                              : containsDocuments
                                ? T.translate('case.docsFoundMsg', {
                                    docsCount: matchesData.documents.length,
                                  })
                                : T.translate('case.noMatchesFoundMsg')}
                          </h4>
                        )}
                      </div>
                    )}
                  </Typography>
                  {itm.user !== 'botIcon' && typeof itm.text === 'string' && (
                    <IconButton
                      className={classes.copyButton}
                      size="small"
                      onClick={async () => {
                        await copyTextToClipboard(itm.text);
                        setCopiedUserTexts((prev) => ({ ...prev, [itemKey]: true }));
                        setTimeout(() => {
                          setCopiedUserTexts((prev) => ({ ...prev, [itemKey]: false }));
                        }, 2000);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <Tooltip
                        title={
                          isCopied ? T.translate('generic.copied') : T.translate('generic.copy')
                        }
                        placement="top"
                      >
                        <ContentCopy sx={{ fontSize: 16, color: '#666' }} />
                      </Tooltip>
                    </IconButton>
                  )}
                </div>
                {/* {itm.user !== 'botIcon' && itm.options && !fileSidebarAskAI && !directAnswers && (
                  <Tooltip
                    title={<SearchOptionsTooltip options={itm.options} />}
                    placement="right"
                    arrow
                  >
                    <Info fontSize="small" sx={{ color: '#85A0F1', ml: '1rem' }} />
                  </Tooltip>
                )} */}
              </div>
              {itm.responseType !== 'file_list' &&
                itm.user === 'botIcon' &&
                itm.relevantFiles &&
                itm.relevantFiles.length > 0 && (
                  <div style={{ marginTop: '0.25rem' }}>
                    <Tooltip title={T.translate('generic.viewSourceFiles')} placement="top" arrow>
                      <Button
                        size="small"
                        startIcon={<Source fontSize="small" sx={{ color: '#85A0F1' }} />}
                        onClick={(e) =>
                          handleToggleSourceMenu(
                            e.currentTarget as HTMLElement,
                            itm.relevantFiles || [],
                            itm.citations || [],
                          )
                        }
                        sx={{
                          textTransform: 'none',
                          pl: 0,
                          border: '1px solid #85A0F1',
                          padding: '1px 4px',
                          margin: '5px 0',
                        }}
                      >
                        {T.translate('generic.sourceFiles')}
                      </Button>
                    </Tooltip>
                  </div>
                )}
              {/* Display filters for direct answers mode */}
              {directAnswers &&
                itm.user === 'botIcon' &&
                Array.isArray(directAnswersFilters) &&
                directAnswersFilters.length > 0 &&
                (() => {
                  // Find matching filter by answerId first, then fallback to searchHistoryId
                  const matchedFilter =
                    directAnswersFilters.find((filter: any) => filter.id === itm.answerId) ||
                    directAnswersFilters.find((filter: any) => filter.id === itm.searchHistoryId);

                  return matchedFilter ? renderDirectAnswersFilters(matchedFilter) : null;
                })()}
              {itm.responseType === 'file_list' &&
                Array.isArray(itm.relevantFiles) &&
                itm.relevantFiles.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <RelevantFilesTable rows={itm.relevantFiles} multiSelect={true} />
                    <div
                      style={{
                        fontSize: '14px',
                        paddingTop: '0.25rem',
                        display: 'flex',
                        marginRight: '1.5rem',
                        gap: '0.5rem',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Typography variant="body1">{T.translate('generic.totalFiles')}</Typography>
                      <Box
                        sx={{
                          backgroundColor: '#F7F7F8',
                          border: '1px solid #85A0F1',
                          borderRadius: '16px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '35px',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        {itm.relevantFiles.length}
                      </Box>
                    </div>
                  </div>
                )}
              {itm.user !== 'botIcon' &&
                itm.location &&
                itm.location !== searchCenterLocation.allMatches &&
                itm.location !== 'All Matches' && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <FilterListOutlined fontSize="small" sx={{ mr: '0.5rem', mb: '0.2rem' }} />
                    <span>{itm.location}</span>
                  </div>
                )}
              {itm.responseType === 'file_list' ? (
                <Accordion
                  elevation={0}
                  sx={{
                    width: '98%',
                    marginTop: '1rem',
                    boxShadow:
                      '0px 1px 2px rgba(0,0,0,0.08), 1px 0px 2px rgba(0,0,0,0.06), -1px 0px 2px rgba(0,0,0,0.06)',
                    '&::before': { display: 'none' },
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body1">
                      {T.translate('generic.viewExtractedInfo')}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div style={{ width: '100%' }}>
                      {itm.user === 'botIcon' && (
                        <Typography
                          variant={fileSidebarAskAI ? 'body1' : 'h6'}
                          style={{
                            lineBreak: 'auto',
                            ...(itm.text === T.translate('case.searchingText') &&
                              searchingTextStyle),
                            fontSize: '13px',
                          }}
                        >
                          <PerfectScrollbar style={{ height: '100%' }}>
                            {itm.command === 'summarize' ? (
                              <DocSummary
                                summary={itm.text}
                                submissions={itm.submissions}
                                rulings={itm.rulings}
                                scrollBarRef={scrollBarRef}
                              />
                            ) : typeof itm.text === 'string' && !itm?.rawText ? (
                              itm.text
                            ) : (
                              <SearchAnswerContainer
                                response={itm.text}
                                rawAnswer={itm?.rawText ? itm.rawText : ''}
                                typingEffect={itm.typingEffect}
                                scrollBarRef={scrollBarRef}
                                handleMatchesLinkClicked={handleMatchesLinkClicked}
                                allMatchesCount={effectiveMatchesCount}
                                allMatches={effectiveMatches}
                                compare={effectiveCompare}
                                files={matchesData?.files}
                                isTranscriptPage={isTranscriptPage}
                                fileSidebarAskAI={fileSidebarAskAI}
                                gridView={gridView}
                                directAnswers={directAnswers}
                              />
                            )}
                          </PerfectScrollbar>
                        </Typography>
                      )}
                    </div>
                  </AccordionDetails>
                </Accordion>
              ) : (
                <div style={{ width: '100%' }}>
                  {itm.user === 'botIcon' && (
                    <Typography
                      variant={fileSidebarAskAI ? 'body1' : 'h6'}
                      style={{
                        lineBreak: 'auto',
                        ...(fileSidebarAskAI && { fontSize: '13px' }),
                        ...(itm.text === T.translate('case.searchingText') && searchingTextStyle),
                      }}
                    >
                      <PerfectScrollbar style={{ height: '100%' }}>
                        {itm.command === 'summarize' ? (
                          <DocSummary
                            summary={itm.text}
                            submissions={itm.submissions}
                            rulings={itm.rulings}
                            scrollBarRef={scrollBarRef}
                          />
                        ) : typeof itm.text === 'string' && !itm?.rawText ? (
                          itm.text
                        ) : (
                          <div>
                            <div>
                              <SearchAnswerContainer
                                response={itm.text}
                                rawAnswer={itm?.rawText ? itm.rawText : ''}
                                typingEffect={itm.typingEffect}
                                scrollBarRef={scrollBarRef}
                                handleMatchesLinkClicked={handleMatchesLinkClicked}
                                allMatchesCount={effectiveMatchesCount}
                                allMatches={effectiveMatches}
                                compare={effectiveCompare}
                                files={matchesData?.files}
                                isTranscriptPage={isTranscriptPage}
                                fileSidebarAskAI={fileSidebarAskAI}
                                gridView={gridView}
                                directAnswers={directAnswers}
                              />
                            </div>
                            {itm.user === 'botIcon' &&
                              (itm.typingEffect || itm?.searchHistoryId) && (
                                <div
                                  style={{
                                    marginTop: '-8px',
                                    marginBottom: '20px',
                                    paddingLeft: '8px',
                                    paddingTop: '0px',
                                    paddingBottom: '5px',
                                    paddingRight: '8px',
                                    // backgroundColor: '#fff',
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: '8px',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Tooltip title={T.translate('generic.goodAnswer')} placement="top" arrow>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleThumbUp(
                                            `answer-${index}`,
                                            itm?.searchHistoryId,
                                            itm?.rawText,
                                            itm?.answerId,
                                          )
                                        }
                                        sx={{
                                          color:
                                            feedbackRating[`answer-${index}`] === 'up'
                                              ? '#4567e5'
                                              : '#707070',
                                          backgroundColor:
                                            feedbackRating[`answer-${index}`] === 'up'
                                              ? 'rgba(69, 103, 229, 0.1)'
                                              : 'transparent',
                                          '&:hover': {
                                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                            color: '#4567e5',
                                          },
                                        }}
                                      >
                                        <ThumbUp fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={T.translate('generic.badAnswer')} placement="top" arrow>
                                      <IconButton
                                        size="small"
                                        onClick={(e) =>
                                          handleThumbDown(
                                            `answer-${index}`,
                                            e,
                                            itm?.searchHistoryId,
                                            itm?.rawText,
                                            itm?.answerId,
                                          )
                                        }
                                        sx={{
                                          color:
                                            feedbackRating[`answer-${index}`] === 'down'
                                              ? '#4567e5'
                                              : '#707070',
                                          backgroundColor:
                                            feedbackRating[`answer-${index}`] === 'down'
                                              ? 'rgba(69, 103, 229, 0.1)'
                                              : 'transparent',
                                          '&:hover': {
                                            backgroundColor: 'rgba(69, 103, 229, 0.1)',
                                            color: '#4567e5',
                                          },
                                        }}
                                      >
                                        <ThumbDown fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {canRestartFromQuestion && (
                                      <Tooltip title={T.translate('generic.tryAgain')} placement="top" arrow>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            if (
                                              previousUserQuestionText &&
                                              onRestartFromQuestion &&
                                              answerFiltersForItem
                                            ) {
                                              onRestartFromQuestion(
                                                previousUserQuestionText,
                                                answerFiltersForItem,
                                              );
                                            }
                                          }}
                                          sx={{
                                            color: '#707070',
                                            padding: '0 0 1px 0',
                                            '&:hover': {
                                              backgroundColor: 'rgba(69, 103, 229, 0.1)',
                                              color: '#4567e5',
                                            },
                                          }}
                                        >
                                          <RestartAltOutlined fontSize="medium" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </div>
                                  {showFeedbackInput[`answer-${index}`] && (
                                    <div style={{ marginTop: '12px' }}>
                                      <Typography
                                        variant="body2"
                                        style={{
                                          marginBottom: '8px',
                                          color: '#555',
                                          fontSize: '13px',
                                        }}
                                      >
                                        {T.translate('case.badFeedbackInstruction')}
                                      </Typography>
                                      <div style={{ position: 'relative' }}>
                                        <TextField
                                          fullWidth
                                          multiline
                                          rows={5}
                                          value={feedbackComment[`answer-${index}`] || ''}
                                          onChange={(e) =>
                                            setFeedbackComment((prev) => ({
                                              ...prev,
                                              [`answer-${index}`]: e.target.value,
                                            }))
                                          }
                                          onKeyDown={(e) =>
                                            handleFeedbackKeyDown(
                                              e,
                                              `answer-${index}`,
                                              itm?.searchHistoryId,
                                              itm?.rawText,
                                              itm?.answerId,
                                            )
                                          }
                                          placeholder={T.translate('case.enterFeedback')}
                                          variant="outlined"
                                          sx={{
                                            '& .MuiOutlinedInput-root': {
                                              backgroundColor: 'white',
                                              fontSize: '14px',
                                              '& fieldset': {
                                                borderColor: '#EFEFEF',
                                              },
                                              '&:hover fieldset': {
                                                borderColor: '#EFEFEF',
                                              },
                                              '&.Mui-focused fieldset': {
                                                borderColor: '#EFEFEF',
                                              },
                                            },
                                          }}
                                        />
                                        <IconButton
                                          onClick={() =>
                                            handleFeedbackSubmit(
                                              `answer-${index}`,
                                              itm?.searchHistoryId,
                                              itm?.rawText,
                                              itm?.answerId,
                                            )
                                          }
                                          disabled={
                                            !feedbackComment[`answer-${index}`] ||
                                            !feedbackComment[`answer-${index}`].trim()
                                          }
                                          sx={{
                                            position: 'absolute',
                                            bottom: '8px',
                                            right: '8px',
                                            padding: '4px',
                                            color: '#707070',
                                            '&:hover': {
                                              backgroundColor: 'rgba(112, 112, 112, 0.1)',
                                            },
                                            '&.Mui-disabled': {
                                              color: '#cccccc',
                                            },
                                          }}
                                        >
                                          <Send fontSize="small" />
                                        </IconButton>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>
                        )}
                      </PerfectScrollbar>
                    </Typography>
                  )}
                </div>
              )}
            </div>
          </div>
        </Grid>
      );
    });
  }, [
    searchAnsChat,
    matchesData,
    copiedUserTexts,
    classes.answersContainer,
    classes.answersSectionAvatar,
    classes.answersSection,
    classes.copyWrapper,
    classes.answersSectionHeading,
    classes.copyButton,
    fileSidebarAskAI,
    directAnswers,
    directAnswersFilters,
    scrollBarRef,
    isTranscriptPage,
    gridView,
    feedbackRating,
    showFeedbackInput,
    feedbackComment,
    copyTextToClipboard,
    handleToggleSourceMenu,
    renderDirectAnswersFilters,
    handleThumbUp,
    handleThumbDown,
    handleFeedbackKeyDown,
    handleFeedbackSubmit,
    directAnswersFilters,
    renderDirectAnswersFilters,
    onRestartFromQuestion,
  ]);
  return (
    <Grid
      container
      height="100%"
      width="100%"
      sx={{
        alignContent: 'end',
        position: 'relative',
        marginTop: fileSidebarAskAI ? '-0.5rem' : '0',
        ...(fileSidebarAskAI && {
          maxHeight: 'calc(100vh - 23rem)',
          ...(gridView && { height: 'calc(100vh - 20rem)' }),
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }),
        flex: 1,
      }}
      id="search-container"
      aria-label={searchContainerId}
    >
      <style>{blinkAnimation}</style>
      <PerfectScrollbar
        options={{
          wheelPropagation: false, // Prevents scroll from propagating to parent
          suppressScrollX: false, // Allow horizontal scrolling for wide content (tables)
        }}
        style={{
          width: '100%',
          overflowX: 'auto',
          flex: 1,
          ...(fileSidebarAskAI && {
            flex: '1 1 auto',
            maxHeight: 'calc(100vh - 5rem)',
            overflowY: 'auto',
            padding: '0.5rem 2rem 0.5rem 0.5rem',
            minHeight: '100px',
          }),
        }}
        containerRef={(ref) => {
          scrollBarRef.current = ref;
          if (ref && fileSidebarAskAI) {
            setTimeout(() => {
              const psElement = ref as any;
              if (psElement._ps) {
                psElement._ps.update();
              }
            }, 100);
          }
        }}
      >
        {isError && (
          <SearchErrorPage errorText={matchesData?.text} startNewSearch={startNewSearch} />
        )}
        {/* {memoizedDirectAnswersFilters} */}
        {memoizedSearchAnswers}
        {memoizedSearchMatches}
      </PerfectScrollbar>
      <Popper
        open={Boolean(sourceMenuAnchorEl)}
        anchorEl={sourceMenuAnchorEl}
        placement={sourceMenuPlacement}
        modifiers={[
          { name: 'flip', options: { fallbackPlacements: ['top-start', 'bottom-start'] } },
          { name: 'preventOverflow', options: { boundary: 'viewport' } },
          { name: 'offset', options: { offset: [0, 8] } },
        ]}
        sx={{ zIndex: 99 }}
      >
        <ClickAwayListener
          onClickAway={(event) => {
            const target = (event as unknown as MouseEvent | TouchEvent).target as Node;
            if (sourceMenuAnchorEl && target && sourceMenuAnchorEl.contains(target)) return;
            handleCloseSourceMenu();
          }}
        >
          <Paper
            elevation={3}
            sx={{ minWidth: 250, maxWidth: 450, maxHeight: '50vh', overflowY: 'auto' }}
          >
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, fontSize: '12px' }}>
                {T.translate('generic.sourceFiles')} ({sourceMenuFiles.length})
                <Box component="span" sx={{ ml: 2 }}>
                  {T.translate('generic.citations')} ({totalSourceMenuCitations})
                </Box>
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  size="small"
                  aria-label={T.translate('generic.close')}
                  onClick={handleCloseSourceMenu}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Divider />
            {sourceMenuFiles && sourceMenuFiles.length > 0 && (
              <>
                <Box sx={{ px: 2, py: 1, pt: 1, pb: 0, display: 'flex', alignItems: 'center' }}>
                  <InfoOutlined fontSize="small" sx={{ color: '#85A0F1', mr: 0.5 }} />
                  <Typography variant="body2" color="text.secondary" fontSize="11px">
                    {T.translate('generic.dragAndDropSourceFilesInfo')}
                  </Typography>
                </Box>
                <Box sx={{ px: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setSourceMenuSelectedIds(sourceMenuFiles.map((f) => f.id))}
                    disabled={sourceMenuSelectedIds.length === sourceMenuFiles.length}
                    sx={{
                      textTransform: 'none',
                      pl: 0,
                      border: '1px solid #85A0F1',
                      padding: '1px 4px',
                      margin: '5px 0',
                      '&.Mui-disabled': { borderColor: '#cdcdcd' },
                    }}
                  >
                    {T.translate('generic.selectAllFiles')}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setSourceMenuSelectedIds([])}
                    disabled={sourceMenuSelectedIds.length === 0}
                    sx={{
                      textTransform: 'none',
                      pl: 0,
                      border: '1px solid #85A0F1',
                      padding: '1px 4px',
                      margin: '5px 0',
                      '&.Mui-disabled': { borderColor: '#cdcdcd' },
                    }}
                  >
                    {T.translate('generic.clearAllFiles')}
                  </Button>
                </Box>
              </>
            )}
            {sourceMenuFiles && sourceMenuFiles.length > 0 ? (
              <List dense>
                {sourceMenuFiles.map((f) => {
                  const isSelected = sourceMenuSelectedIds.includes(f.id);
                  return (
                    <DraggableSourceFileItem
                      key={f.id}
                      file={f}
                      allFiles={sourceMenuFiles}
                      selectedIdsRef={sourceMenuSelectedIdsRef}
                      isSelected={isSelected}
                      citationCount={
                        typeof f.citationsCount === 'number'
                          ? f.citationsCount
                          : sourceMenuCitationsCount[f.id] || 0
                      }
                      onItemClick={() => {
                        setSourceMenuSelectedIds((prev) =>
                          isSelected ? prev.filter((id) => id !== f.id) : [...prev, f.id],
                        );
                      }}
                      onCmdEnterToggle={() => {
                        setSourceMenuSelectedIds((prev) =>
                          prev.includes(f.id) ? prev.filter((id) => id !== f.id) : [...prev, f.id],
                        );
                      }}
                    />
                  );
                })}
              </List>
            ) : (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body2">No source files.</Typography>
              </Box>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
      {isScrolled && (
        <Fab
          color="primary"
          size="small"
          variant="extended"
          className={classes.scrollToTopButton}
          onClick={scrollToTop}
          sx={{
            ...(fileSidebarAskAI && {
              bottom: '5rem',
              right: '1rem',
            }),
          }}
        >
          <KeyboardArrowUp />
        </Fab>
      )}
    </Grid>
  );
}
